
import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Radio,
  Select,
  styled,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState, useMemo } from "react";
import Grid from "@mui/material/Grid2";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import StorageIcon from "@mui/icons-material/Storage";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";
import NetworkCellIcon from "@mui/icons-material/NetworkCell";
import WifiIcon from "@mui/icons-material/Wifi";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useGetSearchFilesQuery } from "../../../store/services/principleApi";
import {
  useGetRcsTemplateViewQuery,
  useGetRcsTemplateListQuery,
  useGetcategoryQuery,
  useGetcategoryidQuery,
  useGetrcsassociatebotidsQuery,
  usePostRcscampaignMutation,
  usePostRcscampaignWithoutFileMutation,
  useRcscampaignwithjustsplitMutation,
  useRcspreviewMutation,
  useGetrcspreviewcountQuery,
} from "../../../store/services/rcstemplateApi";
import { toast } from "react-toastify";
import { jwtDecode } from "jwt-decode";
import Looks3Icon from "@mui/icons-material/Looks3";

// ── Safe text helper ──
// API errors/messages can be strings OR objects like { message: "..." }.
// React cannot render objects (error #31), so always convert to a string first.
const toText = (value, fallback = "") => {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) {
    const joined = value.map((v) => toText(v, "")).filter(Boolean).join(", ");
    return joined || fallback;
  }
  if (typeof value === "object") {
    const inner = value.message ?? value.error ?? value.detail ?? value.msg;
    if (inner !== undefined && inner !== value) return toText(inner, fallback);
    try {
      return JSON.stringify(value);
    } catch {
      return fallback;
    }
  }
  return fallback;
};

// Pulls the best message out of an RTK Query / thrown error
const getErrorMessage = (error, fallback) =>
  toText(error?.data?.message ?? error?.data?.error ?? error?.data ?? error?.message ?? error?.error, fallback);

// Key = text inside [ ] (e.g. VAR1), value = what the user typed.
// Sent as a plain object: {"VAR1":"111","VAR2":"22"}
const toPayloadVariables = (vars) =>
  Object.fromEntries(
    Object.entries(vars).map(([key, value]) => [
      key.replace(/__occurrence_\d+$/, ""),
      value,
    ])
  );

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const PhonePreview = styled(Card)(({ theme }) => ({
  width: "260px",
  height: "460px",
  margin: "0 auto",
  border: "1px solid #E2E8F0",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
  borderRadius: "28px",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  background: "#0F172A",
  padding: "6px",
}));

// ── Base field styling — applied to MuiOutlinedInput-root (works for both
//    TextField and Select). Always-visible border; hover only darkens it.
const fieldSx = {
  height: 35,
  borderRadius: "8px",
  background: "rgba(255,255,255,0.75)",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#CBD5E1",
    borderWidth: "1px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#94A3B8",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#7c3aed",
    borderWidth: "2px",
  },
  "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E2E8F0",
  },
};

// TextField flavor — apply to the TextField's sx
const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    ...fieldSx,
  },
};

// Select flavor — MUST target the inner .MuiOutlinedInput-root
// because FormControl wraps the label + input + helper.
const selectSx = {
  "& .MuiOutlinedInput-root": {
    ...fieldSx,
    // Center the selected text vertically inside the 35px box
    "& .MuiSelect-select": {
      padding: "0 14px",
      fontSize: "0.8rem",
      display: "flex",
      alignItems: "center",
      height: "100%",
      boxSizing: "border-box",
      minHeight: "unset",
    },
    // Tuck the dropdown arrow in a bit so it lines up with the TextField's end-icon
    "& .MuiSelect-icon": {
      right: 8,
      fontSize: "1.15rem",
    },
  },
};

// Small helper — a consistent bordered section wrapper
const SectionBox = ({ title, children, sx }) => (
  <Box
    sx={{
      p: 1.5,
      mb: 2,
      borderRadius: "10px",
      border: "1px solid #E2E8F0",
      backgroundColor: "rgba(255,255,255,0.55)",
      ...sx,
    }}
  >
    {title && (
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.25, color: "#0F172A" }}>
        {title}
      </Typography>
    )}
    {children}
  </Box>
);

const PREVIEW_TERMINAL_STATUSES = [
  "completed",
  "failed",
  "error",
  "success",
  "done",
  "processed",
  "complete",
];
const PREVIEW_POLL_INTERVAL_MS = 2500;

// How bot_id is sent inside multipart FormData.
// JSON payloads always send a real array of strings: ["6","7"]
//   "csv"    -> bot_id=6,7
//   "repeat" -> bot_id=6 & bot_id=7
//   "json"   -> bot_id=["6","7"]
const BOT_ID_FORMDATA_MODE = "csv";
const appendBotIds = (fd, ids) => {
  if (BOT_ID_FORMDATA_MODE === "repeat") ids.forEach((id) => fd.append("bot_id", id));
  else if (BOT_ID_FORMDATA_MODE === "json") fd.append("bot_id", JSON.stringify(ids));
  else fd.append("bot_id", ids.join(","));
};

const RCSComponent = ({ service_id }) => {
  const navigate = useNavigate();

  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [selectedRcsTemplateId, setSelectedRcsTemplateId] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [testMobileNumber, setTestMobileNumber] = useState("");
  const [category, setCategory] = useState("");
  const [categoryName, setCategoryName] = useState("");
  const [errors, setErrors] = useState({});
  const [selectedBotIds, setSelectedBotIds] = useState([]); // array of bot_id strings
  const [selectedFile, setSelectedFile] = useState(null);
  const [dateTime, setDateTime] = useState("");
  const [timeZone, setTimeZone] = useState("Asia/Calcutta");
  const [enableDateTime, setEnableDateTime] = useState(false);
  const [filterDatatill_days, setFilterDatatill_days] = useState("");
  const [TTL, setTTL] = useState("");
  const [TTLTime, setTTLTime] = useState(false);
  const [enableSplit, setEnableSplit] = useState(false);
  const [batchSize, setBatchSize] = useState("");
  const [timeInterval, setTimeInterval] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [currentTime, setCurrentTime] = useState("");
  const [selectedDbFile, setSelectedDbFile] = useState(null);
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [campaignName, setCampaignName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [customerId, setCustomerId] = useState(null);
  const [enableFilterTill, setEnableFilterTill] = useState(false);
  const [templateVariables, setTemplateVariables] = useState({});

  const [previewId, setPreviewId] = useState(null);
  const [previewStatus, setPreviewStatus] = useState(null);
  const [previewData, setPreviewData] = useState(null);

  const { data: getcategory } = useGetcategoryQuery();
  const { currentData: campaignData } = useGetRcsTemplateViewQuery(selectedTemplateId);
  const [postRcscampaign, { isLoading }] = usePostRcscampaignMutation();
  const [postRcscampaignWithoutFile, { isLoading: isTestLoading }] = usePostRcscampaignWithoutFileMutation();
  const [postRcscampaignWithSplit, { isLoading: isSplitLoading }] = useRcscampaignwithjustsplitMutation();
  const [rcsPreview, { isLoading: isPreviewSubmitting }] = useRcspreviewMutation();
  const [testResponse, setTestResponse] = useState(null);

  const {
    data: previewCountData,
    isFetching: isPreviewCountFetching,
    error: previewCountError,
  } = useGetrcspreviewcountQuery(
    previewId ? { preview_id: previewId } : null,
    {
      skip: !previewId,
      pollingInterval:
        previewId && !PREVIEW_TERMINAL_STATUSES.includes(previewStatus?.toLowerCase())
          ? PREVIEW_POLL_INTERVAL_MS
          : 0,
      refetchOnMountOrArgChange: false,
    }
  );

  useEffect(() => {
    const d = previewCountData?.data || previewCountData;
    const status = d?.status;
    if (status) {
      const normalized = String(status).toLowerCase();
      console.log("Preview status updated:", normalized);
      setPreviewStatus(normalized);

      if (PREVIEW_TERMINAL_STATUSES.includes(normalized)) {
        setPreviewData(d);
      }
    }
  }, [previewCountData]);

  useEffect(() => {
    if (previewCountError) {
      console.error("Preview count error:", previewCountError);
    }
  }, [previewCountError]);

  const isPreviewLoading =
    isPreviewSubmitting || (!!previewId && !PREVIEW_TERMINAL_STATUSES.includes(previewStatus?.toLowerCase()));

  const { data: getallagents } = useGetcategoryidQuery(category, {
    skip: !category,
  });

  // ── Multi-bot selection ──
  const allBots = useMemo(() => getallagents?.data || [], [getallagents]);
  const allBotIds = useMemo(() => allBots.map((b) => String(b.bot_id)), [allBots]);
  const selectedBots = useMemo(
    () => allBots.filter((b) => selectedBotIds.includes(String(b.bot_id))),
    [allBots, selectedBotIds]
  );

  const {
    currentData: associatedBotsData,
    isSuccess: associatedBotsLoaded,
  } = useGetrcsassociatebotidsQuery(
    { rcs_template_id: selectedRcsTemplateId },
    { skip: !selectedRcsTemplateId }
  );

  const associatedBotIds = useMemo(() => {
    const response = associatedBotsData?.data ?? associatedBotsData;
    const entries = Array.isArray(response)
      ? response
      : response?.bot_ids ?? response?.botIds ?? response?.associated_bot_ids ??
        response?.rcs_customer_bot_ids ?? response?.associated_bots ?? response?.bots;
    if (!Array.isArray(entries)) return null;
    return new Set(
      entries
        .flatMap((bot) =>
          typeof bot === "object" && bot !== null
            ? [bot.bot_id, bot.rcs_customer_bot_id, bot.id]
            : [bot]
        )
        .filter((id) => id !== null && id !== undefined)
        .map(String)
    );
  }, [associatedBotsData]);

  // Bots the user may pick: all bots, or only the ones linked to the chosen template
  const selectableBotIds = useMemo(() => {
    if (!selectedRcsTemplateId || !associatedBotIds) return allBotIds;
    return allBots
      .filter(
        (bot) =>
          associatedBotIds.has(String(bot.bot_id)) ||
          associatedBotIds.has(String(bot.rcs_customer_bot_id))
      )
      .map((bot) => String(bot.bot_id));
  }, [allBots, allBotIds, associatedBotIds, selectedRcsTemplateId]);

  const botIdList = selectedBots.map((b) => String(b.bot_id)); // array of strings -> goes to the API
  const agent = selectedBots[0] || ""; // primary bot (first selected) — used for templates, route, agent name
  const allSelected =
    selectableBotIds.length > 0 &&
    selectableBotIds.every((id) => selectedBotIds.includes(id));
  const someSelected = selectedBotIds.length > 0 && !allSelected;

  // ── Internal (numeric) bot IDs, sorted & de-duplicated — used for the
  //    selected_bot_ids / reference_rcs_template_id payload fields.
  const templateQueryBotIds = useMemo(
    () =>
      [
        ...new Set(
          selectedBots.map((bot) => Number(bot.rcs_customer_bot_id))
        ),
      ].sort((a, b) => a - b),
    [selectedBots]
  );

  // Select every bot by default whenever the bot list loads or the category changes
  useEffect(() => {
    setSelectedBotIds(allBotIds);
    setSelectedTemplateId(null);
    setSelectedRcsTemplateId(null);
    setSelectedTemplate(null);
  }, [allBotIds]);

  // When a template is selected, pre-select the bots associated with it.
  // This does NOT depend on selectedBotIds, so a manual deselect afterwards is kept.
  useEffect(() => {
    if (!selectedRcsTemplateId || !associatedBotsLoaded || !associatedBotIds) return;
    setSelectedBotIds(
      allBots
        .filter(
          (bot) =>
            associatedBotIds.has(String(bot.bot_id)) ||
            associatedBotIds.has(String(bot.rcs_customer_bot_id))
        )
        .map((bot) => String(bot.bot_id))
    );
  }, [allBots, associatedBotIds, associatedBotsLoaded, selectedRcsTemplateId]);

  // The user can freely deselect/reselect bots. The template is kept.
  // If a template is selected, only bots linked to it can be selected.
  const handleBotChange = (e) => {
    let value = e.target.value;
    value = typeof value === "string" ? value.split(",") : value;

    const next = value.includes("__all__")
      ? allSelected
        ? []
        : selectableBotIds
      : value;

    const ordered = allBotIds.filter(
      (id) => next.includes(id) && selectableBotIds.includes(id)
    );

    setSelectedBotIds(ordered);
    setErrors((prev) => ({ ...prev, agent: undefined }));
  };

  const {
    data: templatesData,
    isLoading: templatesLoading,
    error: templatesError,
  } = useGetRcsTemplateListQuery(
    { id: customerId, page: 1, limit: 100 },
    { skip: !customerId }
  );
  const { data: dbFiles, isLoading: dbFilesLoading } = useGetSearchFilesQuery();

  const templateData = useMemo(
    () => (templatesData?.data || []).filter((template) => template.status?.toLowerCase() === "approved"),
    [templatesData]
  );

  const normalizedTemplates = useMemo(
    () =>
      templateData.map((t) => ({
        _id: t.template_id || t.rcs_template_id || t.id,
        internalTemplateId: t.rcs_template_id,
        templateName: t.template_name,
        templateType: t.rcs_template_type?.toLowerCase().replace(/\s+/g, "_"),
        agent: t.agent,
        status: t.status,
        createdAt: null,
        raw: t,
      })),
    [templateData]
  );

  const mapRcsSuggestions = (...sources) => {
    const buttons = sources.find((source) => Array.isArray(source) && source.length) || [];
    return buttons
      .map((button, index) => ({
        suggestionId: button?.suggestionId ?? button?.suggestion_id ?? button?.button_no ?? index,
        suggestionText: toText(
          typeof button === "string"
            ? button
            : button?.suggestion_text ?? button?.suggestionText ?? button?.text ?? button?.label
        ),
        actionType: button?.actionType ?? button?.button_action_id,
        url: button?.url,
        postback: button?.postback ?? button?.suggestion_postback,
      }))
      .filter((button) => button.suggestionText);
  };

  const mapTemplateForPreview = (template, details = null) => {
    const t = {
      ...template.raw,
      ...(details?.templateDetails || {}),
      buttons: details?.buttonInformation || template.raw.buttons,
      buttonInformation: details?.buttonInformation || template.raw.buttonInformation,
      carousel_cards: details?.carousel_cards || template.raw.carousel_cards,
    };
    const type = template.templateType;

    const mappedTemplate = {
      ...template,
      templateName: template.templateName,
      templateType: type,
    };

    if (type === "text") {
      mappedTemplate.textContent = t.rcs_text;
      mappedTemplate.suggestionsList = mapRcsSuggestions(
        details?.buttonInformation,
        details?.buttons,
        t.buttons,
        t.buttonInformation,
        template.raw.buttons,
        template.raw.buttonInformation,
      );
    }

    if (type === "rich_card") {
      mappedTemplate.richCardStandAloneDetails = {
        media: t.rich_card_image ? { url: t.rich_card_image } : null,
        mediaHeight:
          t.media_details === "Short"
            ? "SHORT_HEIGHT"
            : t.media_details === "Medium"
              ? "MEDIUM_HEIGHT"
              : "TALL_HEIGHT",
        cardTitle: t.rich_card_title,
        cardDescription: t.rich_card_description,
        suggestionsList: mapRcsSuggestions(
          details?.buttonInformation,
          details?.buttons,
          t.buttons,
          t.buttonInformation,
          template.raw.buttons,
          template.raw.buttonInformation,
        ),
      };
    }

    if (type === "carousel") {
      mappedTemplate.carouselCards = (t.carousel_cards || []).map((c, index) => ({
        cardMedia: c.carousel_image ? { url: c.carousel_image } : null,
        cardTitle: c.carousel_title || "",
        cardDescription: c.carousel_description || "",
        suggestionsList: mapRcsSuggestions(
          c.suggestionsList,
          c.buttons,
          c.buttonInformation,
          details?.buttonInformation,
          details?.buttons,
          t.buttonInformation,
          t.buttons,
          template.raw.buttonInformation,
          template.raw.buttons,
        ),
      }));
    }

    return mappedTemplate;
  };

  const getTemplateVariableKey = (name, occurrence) =>
    occurrence === 1 ? name : `${name}__occurrence_${occurrence}`;

  // Label = the text inside [ ] (e.g. "VAR"); the box holds the value.
  const formatTemplateVariableLabel = (key) =>
    key.replace(/__occurrence_\d+$/, "");

  const extractTemplateVariables = (template) => {
    if (!template) return [];
    const texts = [
      template.textContent,
      template.richCardStandAloneDetails?.cardTitle,
      template.richCardStandAloneDetails?.cardDescription,
      ...(template.carouselCards || []).flatMap((c) => [c.cardTitle, c.cardDescription]),
    ].filter(Boolean);

    const maxOccurrences = new Map();
    const regex = /\[\s*([^[]+?)\s*\]/g;
    texts.forEach((text) => {
      const occurrencesInText = new Map();
      let match;
      while ((match = regex.exec(text)) !== null) {
        const name = match[1].trim();
        const occurrence = (occurrencesInText.get(name) || 0) + 1;
        occurrencesInText.set(name, occurrence);
        maxOccurrences.set(
          name,
          Math.max(maxOccurrences.get(name) || 0, occurrence),
        );
      }
    });
    return Array.from(maxOccurrences, ([name, count]) =>
      Array.from({ length: count }, (_, index) =>
        getTemplateVariableKey(name, index + 1),
      ),
    ).flat();
  };

  const buildInitialVariableValues = (varNames) => {
    const initialValues = {};
    varNames.forEach((v) => {
      initialValues[v] = "";
    });
    return initialValues;
  };

  const handleVariableChange = (varName, value) => {
    setTemplateVariables((prev) => ({ ...prev, [varName]: value }));
  };

  const handleBatchSizeChange = (e) => {
    const value = e.target.value;
    setBatchSize(value);
    setErrors((prev) => ({
      ...prev,
      batchSize: value && Number(value) < 1000 ? "Minimum batch size is 1000" : undefined,
    }));
  };

  const handleTimeIntervalChange = (e) => {
    const value = e.target.value;
    setTimeInterval(value);
    setErrors((prev) => ({
      ...prev,
      timeInterval: value && Number(value) < 10 ? "Minimum interval is 10 minutes" : undefined,
    }));
  };

  const substituteVariables = (text) => {
    if (!text) return text;
    const occurrences = new Map();
    return text.replace(/\[\s*([^[]+?)\s*\]/g, (placeholder, rawName) => {
      const name = rawName.trim();
      const occurrence = (occurrences.get(name) || 0) + 1;
      occurrences.set(name, occurrence);
      const value = templateVariables[getTemplateVariableKey(name, occurrence)];
      return value === undefined || value === null || value === ""
        ? placeholder
        : value;
    });
  };

  useEffect(() => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        toast.error("Access token not found in local storage");
        return;
      }
      const decoded = jwtDecode(token);
      const id = decoded?.customer_id || decoded?.id || decoded?.userid || null;
      if (!id) {
        toast.error("Customer ID not found in decoded token");
      } else {
        setCustomerId(id);
      }
    } catch (err) {
      console.error("Error decoding token:", err);
      toast.error("Invalid access token format");
    }
  }, []);

  const filteredTemplates = normalizedTemplates.filter((template) => {
    const matchesSearch =
      template.templateName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.templateType?.includes(searchTerm.toLowerCase());
    const matchesStatus = template.status === "approved";
    return matchesStatus && matchesSearch;
  });

  const timeZones = Intl.supportedValuesOf
    ? Intl.supportedValuesOf("timeZone")
    : [
      "UTC",
      "America/New_York",
      "Europe/London",
      "Asia/Kolkata",
      "Asia/Tokyo",
      "Asia/Calcutta",
    ];

  const getMinDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  useEffect(() => {
    if (selectedTemplateId && templateData.length > 0) {
      const template = normalizedTemplates.find((t) => t._id === selectedTemplateId);
      if (!template) return;
      const mapped = mapTemplateForPreview(template, campaignData?.data);
      setSelectedTemplate(mapped);
      setCarouselIndex(0);
      setTemplateVariables(buildInitialVariableValues(extractTemplateVariables(mapped)));
    } else {
      setSelectedTemplate(null);
      setCarouselIndex(0);
      setTemplateVariables({});
    }
  }, [selectedTemplateId, templateData, campaignData]);

  useEffect(() => {
    if (selectedTemplate?.templateType?.toLowerCase() === "carousel") {
      setCarouselIndex(0);
    }
  }, [selectedTemplate]);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const hours = now.getHours().toString().padStart(2, "0");
      const minutes = now.getMinutes().toString().padStart(2, "0");
      setCurrentTime(`${hours}:${minutes}`);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const validateTestNumbers = () => {
    if (!testMobileNumber) return "Mobile number is required";
    const numbers = testMobileNumber.split(",");
    if (numbers.length > 5) return "Maximum 5 mobile numbers allowed";
    for (let num of numbers) {
      if (num.length !== 10) return "Each mobile number must be exactly 10 digits";
    }
    return null;
  };

  const handleTestMobileChange = (e) => {
    let value = e.target.value;
    value = value.replace(/[^0-9,]/g, "");
    const numbers = value.split(",");
    if (numbers.length > 5) return;
    const validNumbers = numbers.map((num) => num.slice(0, 10));
    setTestMobileNumber(validNumbers.join(","));
  };

  const handleTestCampaign = async () => {
    if (!campaignName || !selectedTemplateId || !agent) {
      toast.error("Please fill all required fields");
      return;
    }
    const numbers = testMobileNumber
      .split(",")
      .map((n) => n.trim())
      .filter(Boolean);
    if (!numbers.length) {
      toast.error("Enter at least one mobile number");
      return;
    }
    if (numbers.length > 5) {
      toast.error("Maximum 5 mobile numbers allowed");
      return;
    }
    const invalidNumber = numbers.find((n) => n.length !== 10);
    if (invalidNumber) {
      toast.error("Each mobile number must be exactly 10 digits");
      return;
    }

    const payload = {
      campaign_name: campaignName,
      customerid: customerId,
      category_id: category,
      category_name: categoryName,
      template_id: selectedTemplateId,
      template_name: selectedTemplate?.templateName || "",
      template_type: selectedTemplate?.templateType || "",
      agent_name: agent?.agent_name || "",
      bot_id: templateQueryBotIds,
      route_id: agent?.route_id,
      time_to_send: enableDateTime ? dateTime : "",
      time_zone: enableDateTime ? timeZone : "",
      ttl: Number(TTL) || 0,
      ttl_time_type: TTLTime ? "hours" : "days",
      filter_till_days: Number(filterDatatill_days) || 0,
      country_id: 1,
      country_code: "+91",
      service_id: service_id || 2,
      mobile_numbers: numbers.join(","),
      test_campaign: true,
      operator_name: agent?.route_id === 6 ? "RCS_pinc_vi" : "RCS_jio",
      smstext: substituteVariables(selectedTemplate?.textContent || selectedTemplate.richCardStandAloneDetails
        ?.cardDescription || (selectedTemplate.carouselCards?.[0]?.cardDescription) || ""),
      template_variables: toPayloadVariables(templateVariables),
      ...(enableSplit ? { batch_size: Number(batchSize) || 0, time_interval: Number(timeInterval) || 0 } : {}),
    };

    try {
      const res = enableSplit
        ? await postRcscampaignWithSplit(payload).unwrap()
        : await postRcscampaignWithoutFile(payload).unwrap();
      if (!res?.status) {
        toast.error(toText(res?.message, "Test campaign failed"));
        return;
      }
      toast.success(toText(res.message, "Test campaign sent successfully"));
      setTestResponse(res);
      setTestMobileNumber("");
    } catch (error) {
      toast.error(getErrorMessage(error, "Server error"));
    }
  };

  const carouselCards = selectedTemplate?.carouselCards || [];
  const totalCards = carouselCards.length;
  const currentCard = carouselCards.length > 0 ? carouselCards[carouselIndex] : null;

  const handleNextCard = () => {
    if (totalCards > 0) setCarouselIndex((prevIndex) => (prevIndex + 1) % totalCards);
  };

  const handlePrevCard = () => {
    if (totalCards > 0) setCarouselIndex((prevIndex) => (prevIndex - 1 + totalCards) % totalCards);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeselect = () => {
    setSelectedTemplateId(null);
    setSelectedRcsTemplateId(null);
    setSelectedTemplate(null);
    setCarouselIndex(0);
    setTemplateVariables({});
  };

  const toggleDrawer = (newOpen) => () => {
    setOpenDrawer(newOpen);
    if (!newOpen) setSearchTerm("");
  };

  const handleFileChange = (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      setSelectedDbFile(null);
      return;
    }
    const selectedFile = files[0];
    setSelectedDbFile(null);
    setSelectedFile(selectedFile);
  };

  const buildCampaignSubmission = () => {
    const previewFilePath = previewData?.FirstApiPath || null;

    // ── Derive the internal bot IDs (rcs_customer_bot_id) and the reference
    //    rcs_template_id that the backend expects alongside the payload.
    const selectedInternalBotIds = [
      ...new Set(
        selectedBots.map((bot) => Number(bot.rcs_customer_bot_id))
      ),
    ].sort((a, b) => a - b);

    const referenceInternalTemplateId = Number(
      selectedTemplate?.internalTemplateId
    );

    if (
      selectedInternalBotIds.length === 0 ||
      selectedInternalBotIds.some((id) => !Number.isSafeInteger(id) || id <= 0)
    ) {
      throw new Error("Please select valid campaign bots");
    }

    if (
      !Number.isSafeInteger(referenceInternalTemplateId) ||
      referenceInternalTemplateId <= 0
    ) {
      throw new Error(
        "Selected template is missing its internal rcs_template_id"
      );
    }

    const formData = new FormData();
    formData.append("selected_bot_ids", JSON.stringify(templateQueryBotIds));
    formData.append(
      "reference_rcs_template_id",
      String(referenceInternalTemplateId)
    );
    formData.append("campaign_name", campaignName);
    formData.append("customerid", customerId);
    formData.append("category_name", categoryName);
    formData.append("template_name", selectedTemplate?.templateName || "");
    formData.append("template_type", selectedTemplate?.templateType || "");
    formData.append("agent_name", agent?.agent_name || "");
    formData.append("template_id", selectedTemplateId);
    formData.append("test_campaign", testMobileNumber || "");
    formData.append("time_to_send", enableDateTime ? dateTime : "");
    formData.append("time_zone", enableDateTime ? timeZone : "");
    formData.append("country_id", 1);
    formData.append("country_code", "+91");
    formData.append("post_via", enableSplit ? "Split" : "RCS");
    appendBotIds(formData, botIdList);
    formData.append("category_id", category || "");
    formData.append("ttl", TTL || 0);
    formData.append("filter_till_days", filterDatatill_days || 0);
    formData.append("ttl_time_type", TTLTime ? "hours" : "days");
    formData.append("service_id", service_id || 2);
    formData.append("route_id", agent?.route_id || 6);
    formData.append("operator_name", agent?.operater_name || "");
    formData.append(
      "smstext",
      substituteVariables(
        selectedTemplate?.textContent ||
        selectedTemplate?.richCardStandAloneDetails?.cardDescription ||
        selectedTemplate?.carouselCards?.[0]?.cardDescription ||
        ""
      )
    );
    formData.append("template_variables", JSON.stringify(toPayloadVariables(templateVariables)));

    if (selectedDbFile?.path) {
      formData.append("upload_file_path", selectedDbFile.path);
      formData.append("filter_name", selectedDbFile.filter_name || "");
    }

    if (previewFilePath) {
      formData.append("file_path", previewFilePath);
    } else if (selectedDbFile) {
      formData.append("file_path", selectedDbFile.path);
    } else if (selectedFile) {
      formData.append("upload_file", selectedFile);
    }

    if (previewId) formData.append("preview_id", previewId);
    if (previewData) {
      formData.append("batch_number", previewData.processing?.batch_number ?? 0);
      formData.append("processed_rows", previewData.processing?.processed_rows ?? 0);
      formData.append("total_numbers", previewData.total_numbers ?? 0);
      formData.append("total_posted", previewData.results?.total_posted ?? 0);
      formData.append("total_invalid", previewData.results?.total_invalid ?? 0);
      formData.append("total_duplicates", previewData.results?.total_duplicates ?? 0);
      formData.append("total_customer_blacklist", previewData.results?.total_customer_blacklist ?? 0);
      formData.append("total_global_blacklist", previewData.results?.total_global_blacklist ?? 0);
      formData.append("last_day_campaign", previewData.results?.last_day_campaign ?? 0);
      formData.append("total_remains", previewData.results?.total_remains ?? 0);
      formData.append("is_campaign_ready", previewData.is_campaign_ready ?? false);
      formData.append("filtered_file_name", previewData.file?.filtered_file_name || "");
      formData.append("original_file_name", previewData.file?.original_name || "");
    }

    if (enableSplit) {
      formData.append("batch_size", batchSize || 0);
      formData.append("time_interval", timeInterval || 0);
    }

    const jsonPayload = {
      selected_bot_ids: templateQueryBotIds,
      reference_rcs_template_id: referenceInternalTemplateId,
      campaign_name: campaignName,
      customerid: customerId,
      category_name: categoryName,
      template_name: selectedTemplate?.templateName || "",
      template_type: selectedTemplate?.templateType || "",
      agent_name: agent?.agent_name || "",
      template_id: selectedTemplateId,
      test_campaign: testMobileNumber || "",
      time_to_send: enableDateTime ? dateTime : "",
      time_zone: enableDateTime ? timeZone : "",
      country_id: 1,
      country_code: "+91",
      post_via: "Split",
      bot_id: botIdList,
      category_id: category || "",
      ttl: TTL || 0,
      filter_till_days: filterDatatill_days || 0,
      ttl_time_type: TTLTime ? "hours" : "days",
      service_id: service_id || 2,
      route_id: agent?.route_id || 6,
      operator_name: agent?.operater_name || "",
      smstext: substituteVariables(
        selectedTemplate?.textContent ||
        selectedTemplate?.richCardStandAloneDetails?.cardDescription ||
        selectedTemplate?.carouselCards?.[0]?.cardDescription ||
        ""
      ),
      template_variables: toPayloadVariables(templateVariables),
      batch_size: Number(batchSize) || 0,
      time_interval: Number(timeInterval) || 0,
      ...(previewFilePath
        ? { file_path: previewFilePath }
        : selectedDbFile
          ? { file_path: selectedDbFile.path }
          : {}),
      ...(selectedDbFile?.path
        ? {
          upload_file_path: selectedDbFile.path,
          filter_name: selectedDbFile.filter_name || "",
        }
        : {}),
      ...(previewId ? { preview_id: previewId } : {}),
      ...(previewData
        ? {
          batch_number: previewData.processing?.batch_number ?? 0,
          processed_rows: previewData.processing?.processed_rows ?? 0,
          total_numbers: previewData.total_numbers ?? 0,
          results: previewData.results || {},
          is_campaign_ready: previewData.is_campaign_ready ?? false,
          filtered_file_name: previewData.file?.filtered_file_name || "",
          original_file_name: previewData.file?.original_name || "",
        }
        : {}),
    };

    const useJsonPayload = enableSplit && !selectedFile && !previewFilePath;

    return { formData, jsonPayload, body: useJsonPayload ? jsonPayload : formData };
  };

  const getCampaignFormErrors = () => {
    const newErrors = {};
    if (!campaignName.trim()) newErrors.campaignName = "Required";
    if (!selectedTemplateId) newErrors.template = "Required";
    if (!agent) newErrors.agent = "Required";
    if (!selectedFile && !selectedDbFile) newErrors.file = "Required";

    if (enableSplit) {
      if (!batchSize || Number(batchSize) < 1000) newErrors.batchSize = "Minimum batch size is 1000";
      if (!timeInterval || Number(timeInterval) < 10) newErrors.timeInterval = "Minimum interval is 10 minutes";
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = getCampaignFormErrors();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fill all required fields");
      return;
    }

    try {
      const { body } = buildCampaignSubmission();
      let response;
      console.debug("Submitting campaign", { enableSplit, selectedFile, selectedDbFile, previewData });
      if (enableSplit) {
        response = await postRcscampaignWithSplit(body).unwrap();
      } else {
        response = await postRcscampaign(body).unwrap();
      }

      if (response.status === false) {
        toast.error(toText(response.message, "Error!"));
        return;
      }
      toast.success("Campaign submitted!");
      setTimeout(() => navigate("/rcs-campaign-list"), 1000);
    } catch (error) {
      toast.error(getErrorMessage(error, "Server error!"));
    }
  };

  const handlePreviewOpen = async () => {
    if (!selectedTemplate) {
      toast.info("Please select a template to preview");
      return;
    }

    const newErrors = getCampaignFormErrors();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fill all required fields");
      return;
    }

    setPreviewId(null);
    setPreviewStatus(null);
    setPreviewData(null);
    setIsPreviewOpen(true);

    try {
      const { body } = buildCampaignSubmission();

      console.log("Submitting preview request...");
      const res = await rcsPreview(body).unwrap();
      console.log("Preview response:", res);

      const d = res?.data || res;
      const id = d?.preview_id || d?.id;
      if (!id) {
        toast.error(toText(res?.message, "Failed to start preview - no preview_id returned"));
        return;
      }

      console.log("Setting preview ID:", id);
      setPreviewId(id);

      const initialStatus = String(d?.status || "queued").toLowerCase();
      setPreviewStatus(initialStatus);

      if (PREVIEW_TERMINAL_STATUSES.includes(initialStatus)) {
        setPreviewData(d);
      }
    } catch (error) {
      console.error("Preview submission error:", error);
      toast.error(getErrorMessage(error, "Failed to generate preview"));
    }
  };

  const handleClosePreviewDialog = () => {
    setIsPreviewOpen(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return dayjs(dateString).format("MMM DD, YYYY");
  };

  const DrawerList = (
    <Box sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Select Template
        </Typography>
        <IconButton onClick={toggleDrawer(false)} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <TextField
        label="Search..."
        type="search"
        variant="outlined"
        size="small"
        fullWidth
        sx={{ mb: 2, ...textFieldSx }}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      <TableContainer
        component={Paper}
        sx={{
          flex: 1,
          borderRadius: "10px",
          border: "1px solid #E2E8F0",
          background: "rgba(255,255,255,0.7)",
          backdropFilter: "blur(8px)",
        }}
      >
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#F8FAFC" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: "60px", py: 1 }}>Select</TableCell>
              <TableCell sx={{ fontWeight: 600, py: 1 }}>Template Name</TableCell>
              <TableCell sx={{ fontWeight: 600, width: "100px", py: 1 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600, width: "100px", py: 1 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, width: "180px", py: 1 }}>Agent</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templatesLoading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            ) : templatesError ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography variant="caption" color="error">Error loading templates</Typography>
                </TableCell>
              </TableRow>
            ) : filteredTemplates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography variant="caption" color="text.secondary">No templates found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredTemplates
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((template) => (
                  <TableRow
                    key={template._id}
                    hover
                    sx={{
                      cursor: "pointer",
                      "&:hover": { backgroundColor: "#F1F5F9" },
                      backgroundColor: selectedTemplateId === template._id ? "#E0F2FE" : "inherit",
                    }}
                    onClick={() => {
                      setSelectedTemplateId(template._id);
                      setSelectedRcsTemplateId(template.internalTemplateId);
                      const mapped = mapTemplateForPreview(template);
                      setSelectedTemplate(mapped);
                      setTemplateVariables(buildInitialVariableValues(extractTemplateVariables(mapped)));
                    }}
                  >
                    <TableCell sx={{ py: 1 }}>
                      <Radio size="small" checked={selectedTemplateId === template._id} />
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="caption" fontWeight={500}>
                        {template.templateName || "Unnamed Template"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: "capitalize" }}>
                        {template.templateType?.toLowerCase() || "Unknown Type"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: template.status?.toLowerCase() === "approved" ? "#2e7d32" : "#d32f2f",
                          fontWeight: 600,
                          textTransform: "capitalize",
                        }}
                      >
                        {template.status || "Unknown"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1, maxWidth: "180px" }}>
                      <Typography variant="caption" color="text.secondary" noWrap title={template.agent || ""}>
                        {template.agent || "-"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
        <Button variant="outlined" onClick={toggleDrawer(false)} size="small">Cancel</Button>
        <Button variant="contained" onClick={toggleDrawer(false)} size="small" disabled={!selectedTemplateId}>
          Select
        </Button>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredTemplates.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          size="small"
        />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ p: 2, minHeight: "100vh" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 2,
          p: 1.5,
          background: "rgba(255,255,255,0.7)",
          borderRadius: "8px",
          border: "1px solid #E2E8F0",
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Create Campaign</Typography>
      </Box>

      <Grid container spacing={2}>
        {/* Left Column - Form */}
        <Grid size={8}>
          <Box
            sx={{
              p: 2,
              borderRadius: "16px",
              border: "1px solid #E2E8F0",
              backgroundColor: "rgba(255,255,255,0.7)",
              mb: 2,
            }}
          >
            {/* Campaign Details */}
            <SectionBox title="Campaign Details">
              <Grid container spacing={1.5}>
                <Grid size={5}>
                  <TextField
                    label="Campaign Name *"
                    value={campaignName}
                    onChange={(e) => setCampaignName(e.target.value)}
                    fullWidth
                    size="small"
                    error={!!errors.campaignName}
                    helperText={errors.campaignName}
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid size={3.5}>
                  <FormControl fullWidth size="small" error={!!errors.category} sx={selectSx}>
                    <InputLabel>Category *</InputLabel>
                    <Select
                      value={category}
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        const selectedItem = getcategory?.data?.find((it) => it.rcs_bot_type_id === selectedId);
                        setCategory(selectedId);
                        setCategoryName(selectedItem?.bot_type_name || "");
                        setSelectedBotIds([]);
                        setSelectedTemplateId(null);
                        setSelectedRcsTemplateId(null);
                        setSelectedTemplate(null);
                      }}
                      label="Category *"
                    >
                      {getcategory?.data?.map((item) => (
                        <MenuItem key={item.rcs_bot_type_id} value={item.rcs_bot_type_id}>
                          {item.bot_type_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={3.5}>
                  <FormControl
                    fullWidth
                    size="small"
                    error={!!errors.agent}
                    disabled={!category}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        ...fieldSx,
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#CBD5E1 !important",
                          borderWidth: "1px",
                        },
                        "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#CBD5E1 !important",
                        },
                        "&:not(.Mui-disabled):hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#94A3B8 !important",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#7c3aed !important",
                          borderWidth: "2px",
                        },
                        "& .MuiSelect-select": {
                          padding: "0 14px",
                          fontSize: "0.8rem",
                          display: "flex",
                          alignItems: "center",
                          height: "100%",
                          boxSizing: "border-box",
                          minHeight: "unset",
                        },
                        "& .MuiSelect-icon": {
                          right: 8,
                          fontSize: "1.15rem",
                        },
                        "&.Mui-disabled .MuiSelect-select": {
                          color: "#666666 !important",
                          WebkitTextFillColor: "#666666 !important",
                        },
                      },
                    }}
                  >
                    <InputLabel sx={{ "&.Mui-disabled": { color: "#666666 !important" } }}>
                      Bots *
                    </InputLabel>
                    <Select
                      multiple
                      value={selectedBotIds}
                      onChange={handleBotChange}
                      label="Bots *"
                      renderValue={(selected) => {
                        if (!selected.length) return "";
                        if (selected.length === allBotIds.length) return `All bots (${selected.length})`;
                        if (selected.length === 1)
                          return allBots.find((b) => String(b.bot_id) === selected[0])?.agent_name || selected[0];
                        return `${selected.length} bots selected`;
                      }}
                      MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                    >
                      {selectableBotIds.length > 1 && (
                        <MenuItem value="__all__" dense>
                          <Checkbox size="small" checked={allSelected} indeterminate={someSelected} />
                          <ListItemText
                            primary="Select all"
                            primaryTypographyProps={{ fontSize: "0.8rem", fontWeight: 600 }}
                          />
                        </MenuItem>
                      )}
                      {allBots.map((item) => {
                        const id = String(item.bot_id);
                        return (
                          <MenuItem
                            key={item.rcs_customer_bot_id}
                            value={id}
                            dense
                            disabled={!selectableBotIds.includes(id)}
                          >
                            <Checkbox size="small" checked={selectedBotIds.includes(id)} />
                            <ListItemText
                              primary={item.agent_name}
                              primaryTypographyProps={{ fontSize: "0.8rem" }}
                            />
                          </MenuItem>
                        );
                      })}
                    </Select>
                    {(!category || errors.agent) && (
                      <FormHelperText sx={{ color: errors.agent ? undefined : "#666666 !important" }}>
                        {!category ? "select a category first" : "Select at least one bot"}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              </Grid>
            </SectionBox>

            {/* Upload & Configure — compact */}
            <SectionBox title="Upload & Configure">
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", gap: 1, width: 220 }}>
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      height: 35,
                      px: 1,
                      textTransform: "none",
                      fontSize: "0.75rem",
                      whiteSpace: "nowrap",
                      background: "rgba(255,255,255,0.7)",
                      backdropFilter: "blur(8px)",
                      borderRadius: "8px",
                      "& .MuiButton-startIcon": { mr: 0.5 },
                      "&:hover": {
                        background: "rgba(255,255,255,0.9)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                      },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                      }}
                    >
                      {selectedFile ? selectedFile.name : "Upload"}
                    </Box>
                    <VisuallyHiddenInput type="file" accept=".csv,.txt,.xlsx" onChange={handleFileChange} />
                  </Button>

                  <Button
                    variant="outlined"
                    startIcon={<StorageIcon />}
                    onClick={() => setIsFileModalOpen(true)}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      height: 35,
                      px: 1,
                      textTransform: "none",
                      fontSize: "0.75rem",
                      whiteSpace: "nowrap",
                      background: "rgba(255,255,255,0.7)",
                      backdropFilter: "blur(8px)",
                      borderRadius: "8px",
                      "& .MuiButton-startIcon": { mr: 0.5 },
                      "&:hover": {
                        background: "rgba(255,255,255,0.9)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                      },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                      }}
                    >
                      {selectedDbFile ? selectedDbFile.filter_name : "Database"}
                    </Box>
                  </Button>
                </Box>

                {/* TTL */}
                <TextField
                  label="TTL"
                  type="number"
                  size="small"
                  sx={{
                    width: 100,
                    ...textFieldSx,
                  }}
                  value={TTL}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || Number(value) >= 0) {
                      setTTL(value);
                    }
                  }}
                  inputProps={{
                    min: 0,
                    onKeyDown: (e) => {
                      if (e.key === "-" || e.key === "e" || e.key === "E") {
                        e.preventDefault();
                      }
                    },
                    onWheel: (e) => {
                      if (Number(TTL) <= 0 && e.deltaY > 0) {
                        e.preventDefault();
                      }
                    },
                  }}
                />

                {/* TTL unit */}
                <Box display="flex" alignItems="center" sx={{ height: 35 }}>
                  <Typography variant="caption" sx={{ mr: 0.5 }}>TTL Unit:</Typography>
                  <Switch checked={TTLTime} onChange={(e) => setTTLTime(e.target.checked)} size="small" />
                  <Typography variant="caption" sx={{ ml: 0.5 }}>{TTLTime ? "Hours" : "Days"}</Typography>
                </Box>

                {/* Filter From LC toggle */}
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={enableFilterTill}
                      onChange={(e) => {
                        setEnableFilterTill(e.target.checked);
                        if (!e.target.checked) setFilterDatatill_days(0);
                      }}
                    />
                  }
                  label="Filter From LC"
                  sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.75rem" }, ml: 1 }}
                />

                {/* Filter Till dropdown — uses selectSx so it matches TextField exactly */}
                {enableFilterTill && (
                  <FormControl size="small" sx={{ width: 130, ...selectSx }}>
                    <InputLabel sx={{ fontSize: "0.7rem" }}>Filter Till</InputLabel>
                    <Select
                      value={filterDatatill_days}
                      label="Filter Till"
                      onChange={(e) => setFilterDatatill_days(e.target.value)}
                      sx={{ fontSize: "0.75rem" }}
                    >
                      <MenuItem value={1} sx={{ fontSize: "0.75rem" }}>Today</MenuItem>
                      <MenuItem value={2} sx={{ fontSize: "0.75rem" }}>Yesterday</MenuItem>
                      {Array.from({ length: 28 }, (_, i) => (
                        <MenuItem key={i + 3} value={i + 3} sx={{ fontSize: "0.75rem" }}>Last {i + 3} days</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>

              {/* Split Scheduler */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  mt: 1.25,
                  flexWrap: "wrap",
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={enableSplit}
                      onChange={(e) => {
                        setEnableSplit(e.target.checked);
                        if (!e.target.checked) {
                          setErrors((prev) => ({ ...prev, batchSize: undefined, timeInterval: undefined }));
                        }
                      }}
                    />
                  }
                  label="Enable Split Scheduler"
                  sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.75rem" }, m: 0 }}
                />
                {enableSplit && (
                  <>
                    <TextField
                      label="Batch Size"
                      type="number"
                      size="small"
                      value={batchSize}
                      onChange={handleBatchSizeChange}
                      error={!!errors.batchSize}
                      helperText={errors.batchSize || "Min 1000"}
                      inputProps={{ min: 1000 }}
                      sx={{
                        width: 150,
                        ...textFieldSx,
                      }}
                    />
                    <TextField
                      label="Interval (minutes)"
                      type="number"
                      size="small"
                      value={timeInterval}
                      onChange={handleTimeIntervalChange}
                      error={!!errors.timeInterval}
                      helperText={errors.timeInterval || "Min 10 min"}
                      inputProps={{ min: 10 }}
                      sx={{
                        width: 150,
                        ...textFieldSx,
                      }}
                    />
                  </>
                )}
              </Box>
            </SectionBox>

            {/* Template Selection */}
            <SectionBox title="Select Template *">
              {!selectedTemplate ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <TextField
                    placeholder="No template selected"
                    size="small"
                    sx={{
                      width: 320,
                      ...textFieldSx,
                    }}
                    disabled
                  />
                  <Button variant="contained" size="small" onClick={toggleDrawer(true)} disabled={!agent}>
                    Browse
                  </Button>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: 1,
                    borderRadius: "8px",
                    border: "1px solid #C8E6C9",
                    background: "rgba(232,245,233,0.55)",
                    maxWidth: 520,
                  }}
                >
                  <DoneIcon sx={{ fontSize: 16, color: "#4caf50" }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {selectedTemplate.templateName || "Unnamed Template"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({selectedTemplate.templateType || "Unknown Type"})
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <IconButton size="small" onClick={handleDeselect} sx={{ p: 0.5 }}>
                    <CloseIcon fontSize="small" color="error" />
                  </IconButton>
                </Box>
              )}
            </SectionBox>

            {/* Dynamic Template Variables */}
            {selectedTemplate && Object.keys(templateVariables).length > 0 && (
              <SectionBox title="Template Variables">
                <Grid container spacing={1.5}>
                  {Object.keys(templateVariables).map((varName) => (
                    <Grid size={4} key={varName}>
                      <TextField
                        label={formatTemplateVariableLabel(varName)}
                        value={templateVariables[varName]}
                        onChange={(e) => handleVariableChange(varName, e.target.value)}
                        size="small"
                        fullWidth
                        sx={textFieldSx}
                      />
                    </Grid>
                  ))}
                </Grid>
              </SectionBox>
            )}

            {/* Test & Schedule */}
            <Grid container spacing={1.5}>
              <Grid size={6}>
                <Box
                  sx={{
                    p: 1.25,
                    borderRadius: "10px",
                    border: "1px solid #E2E8F0",
                    background: "rgba(255,255,255,0.55)",
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "#0F172A" }}>Test</Typography>
                  <TextField
                    label="Mobile Numbers"
                    placeholder="9876543210,9876543211"
                    value={testMobileNumber}
                    onChange={handleTestMobileChange}
                    size="small"
                    fullWidth
                    inputProps={{ inputMode: "numeric" }}
                    helperText="Enter up to 5 numbers, comma separated"
                    sx={{
                      mb: 1,
                      ...textFieldSx,
                    }}
                  />
                  <Button variant="outlined" size="small" onClick={handleTestCampaign} disabled={isTestLoading || isSplitLoading}>
                    {isTestLoading || isSplitLoading ? "Testing..." : "Test"}
                  </Button>
                </Box>
              </Grid>
              <Grid size={6}>
                <Box
                  sx={{
                    p: 1.25,
                    borderRadius: "10px",
                    border: "1px solid #E2E8F0",
                    background: "rgba(255,255,255,0.55)",
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "#0F172A" }}>Schedule</Typography>
                  <FormControlLabel
                    control={<Switch checked={enableDateTime} onChange={(e) => setEnableDateTime(e.target.checked)} size="small" />}
                    label={<Typography variant="caption">Schedule later</Typography>}
                    sx={{ mb: 1 }}
                  />
                  {enableDateTime && (
                    <Grid container spacing={1}>
                      <Grid size={7}>
                        <TextField
                          label="Date & Time"
                          type="datetime-local"
                          value={dateTime}
                          onChange={(e) => setDateTime(e.target.value)}
                          size="small"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ min: getMinDateTime() }}
                          sx={textFieldSx}
                        />
                      </Grid>
                      <Grid size={5}>
                        <FormControl fullWidth size="small" sx={selectSx}>
                          <InputLabel>Time Zone</InputLabel>
                          <Select
                            value={timeZone}
                            onChange={(e) => setTimeZone(e.target.value)}
                            label="Time Zone"
                          >
                            {timeZones.map((tz) => (
                              <MenuItem key={tz} value={tz}>{tz}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Right Column - Preview */}
        <Grid size={4}>
          <PhonePreview>
            <Box sx={{ background: "#FFFFFF", borderRadius: "22px", height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <Box sx={{ width: "100%", height: "28px", px: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", background: "#111827", color: "#E5E7EB", flexShrink: 0 }}>
                <Typography fontSize="0.65rem" opacity={0.8}>{currentTime}</Typography>
                <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                  <WifiIcon sx={{ fontSize: "12px", opacity: 0.8 }} />
                  <NetworkCellIcon sx={{ fontSize: "12px", opacity: 0.8 }} />
                  <BatteryFullIcon sx={{ fontSize: "12px", opacity: 0.8 }} />
                </Box>
              </Box>

              <Box sx={{ width: "100%", height: "48px", px: 1.5, display: "flex", alignItems: "center", borderBottom: "1px solid #E2E8F0", background: "#F1F5F9", flexShrink: 0 }}>
                <IconButton size="small" sx={{ p: 0.5, mr: 1 }}><ArrowBackIcon sx={{ fontSize: 16 }} /></IconButton>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>Business Message</Typography>
              </Box>

              <Box sx={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", minHeight: 0 }}>
                <Box sx={{ flex: 1, p: 1.5, backgroundColor: "#F5F5F5", overflowY: "auto", display: "flex", flexDirection: "column", gap: 1 }}>
                  {selectedTemplate ? (
                    <Box sx={{ width: "100%" }}>
                      {selectedTemplate.templateType?.toLowerCase() === "rich_card" && (
                        <Box sx={{ maxWidth: "90%", mb: 1 }}>
                          <Card sx={{ borderRadius: "8px", overflow: "hidden", border: "1px solid #E2E8F0", background: "rgba(255,255,255,0.7)" }}>
                            {selectedTemplate.richCardStandAloneDetails?.media?.url && (
                              <Box sx={{ height: selectedTemplate.richCardStandAloneDetails.mediaHeight === "SHORT_HEIGHT" ? "80px" : selectedTemplate.richCardStandAloneDetails.mediaHeight === "MEDIUM_HEIGHT" ? "120px" : "150px", overflow: "hidden" }}>
                                <img src={selectedTemplate.richCardStandAloneDetails.media.url} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              </Box>
                            )}
                            <Box sx={{ p: 1.5 }}>
                              {selectedTemplate.richCardStandAloneDetails?.cardTitle && (
                                <Typography variant="subtitle2" fontSize="12px" fontWeight="bold" gutterBottom>{substituteVariables(selectedTemplate.richCardStandAloneDetails.cardTitle)}</Typography>
                              )}
                              {selectedTemplate.richCardStandAloneDetails?.cardDescription && (
                                <Typography variant="body2" fontSize="11px" color="text.secondary" sx={{ mb: 1, whiteSpace: "pre-line", lineHeight: 1.4 }}>{substituteVariables(selectedTemplate.richCardStandAloneDetails.cardDescription)}</Typography>
                              )}
                              {selectedTemplate.richCardStandAloneDetails?.suggestionsList?.length > 0 && (
                                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                                  {selectedTemplate.richCardStandAloneDetails.suggestionsList.map((suggestion, idx) => (
                                    <Button key={suggestion.suggestionId || idx} variant="outlined" size="small" sx={{ fontSize: "10px", p: "4px 8px", minWidth: "auto", borderRadius: "6px" }}>
                                      {toText(suggestion.suggestionText)}
                                    </Button>
                                  ))}
                                </Box>
                              )}
                            </Box>
                          </Card>
                        </Box>
                      )}

                      {selectedTemplate.templateType?.toLowerCase() === "carousel" && currentCard && (
                        <Box sx={{ height: "280px", display: "flex", flexDirection: "column" }}>
                          <Box sx={{ background: "rgba(255,255,255,0.7)", borderRadius: "8px", border: "1px solid #E2E8F0", p: 1 }}>
                            {totalCards > 1 && (
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                                <IconButton size="small" onClick={handlePrevCard} sx={{ p: 0.5, backgroundColor: "rgba(0,0,0,0.05)" }}>
                                  <ChevronLeftIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                                <Typography variant="caption" fontSize="10px">Card {carouselIndex + 1} of {totalCards}</Typography>
                                <IconButton size="small" onClick={handleNextCard} sx={{ p: 0.5, backgroundColor: "rgba(0,0,0,0.05)" }}>
                                  <ChevronRightIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Box>
                            )}
                            <Box>
                              {currentCard.cardMedia?.url && (
                                <Box sx={{ height: "120px", overflow: "hidden", borderRadius: "8px", mb: 1 }}>
                                  <img src={currentCard.cardMedia.url} alt="Card" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                </Box>
                              )}
                              {currentCard.cardTitle && (
                                <Typography variant="subtitle2" fontSize="12px" fontWeight="bold" gutterBottom>{substituteVariables(currentCard.cardTitle)}</Typography>
                              )}
                              {currentCard.cardDescription && (
                                <Typography variant="body2" fontSize="11px" color="text.secondary" sx={{ mb: 1, lineHeight: 1.4 }}>{substituteVariables(currentCard.cardDescription)}</Typography>
                              )}
                              {currentCard.suggestionsList?.length > 0 && (
                                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                                  {currentCard.suggestionsList.map((suggestion, idx) => (
                                    <Button key={suggestion.suggestionId || idx} variant="outlined" size="small" sx={{ fontSize: "10px", p: "4px 8px", minWidth: "auto" }}>
                                      {toText(suggestion.suggestionText)}
                                    </Button>
                                  ))}
                                </Box>
                              )}
                            </Box>
                            {totalCards > 1 && (
                              <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5, mt: 1.5 }}>
                                {Array.from({ length: totalCards }).map((_, idx) => (
                                  <Box key={idx} onClick={() => setCarouselIndex(idx)} sx={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: idx === carouselIndex ? "#2196f3" : "#ccc", cursor: "pointer" }} />
                                ))}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      )}

                      {selectedTemplate.templateType?.toLowerCase() === "text" && (
                        <Box sx={{ alignSelf: "flex-end", maxWidth: "80%", ml: "auto", mb: 1 }}>
                          <Box sx={{ p: 1.2, backgroundColor: "#DCFCE7", borderRadius: "12px 12px 0 12px", border: "1px solid #C5E1A5" }}>
                            <Typography variant="body2" fontSize="12px" sx={{ whiteSpace: "pre-line", lineHeight: 1.4 }}>{substituteVariables(selectedTemplate.textContent) || "Text message content"}</Typography>
                          </Box>
                          {selectedTemplate.suggestionsList?.length > 0 && (
                            <Box sx={{ display: "grid", gap: 0.5, mt: 0.5 }}>
                              {selectedTemplate.suggestionsList.map((suggestion, idx) => (
                                <Button
                                  key={suggestion.suggestionId ?? idx}
                                  variant="outlined"
                                  size="small"
                                  fullWidth
                                  sx={{
                                    fontSize: "10px",
                                    textTransform: "none",
                                    bgcolor: "#fff",
                                    borderColor: "#d7e0e8",
                                  }}
                                >
                                  {suggestion.suggestionText}
                                </Button>
                              ))}
                            </Box>
                          )}
                        </Box>
                      )}

                      {!["rich_card", "carousel", "text"].includes(selectedTemplate.templateType?.toLowerCase()) && (
                        <Box sx={{ textAlign: "center", p: 2 }}>
                          <Typography variant="body2" fontWeight={500} color="primary">{selectedTemplate.templateName}</Typography>
                          <Typography variant="caption" color="text.secondary">{selectedTemplate.templateType}</Typography>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center", p: 2, color: "text.secondary" }}>
                      <Looks3Icon sx={{ fontSize: 28, color: "#CBD5E1", mb: 1 }} />
                      <Typography variant="caption" display="block">Select template</Typography>
                      <Typography variant="caption" display="block">to preview</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </PhonePreview>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Box sx={{ display: "flex", gap: 1, mt: 2, p: 1.5, backgroundColor: "rgba(255,255,255,0.7)", backdropFilter: "blur(8px)", borderRadius: "8px", border: "1px solid #E2E8F0" }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<VisibilityIcon fontSize="small" />}
          onClick={handlePreviewOpen}
          disabled={isPreviewSubmitting}
          sx={{ minWidth: 110 }}
        >
          Preview
        </Button>
      </Box>

      {/* Live Campaign Preview Modal */}
      <Dialog open={isPreviewOpen} onClose={handleClosePreviewDialog} fullWidth maxWidth="md">
        <DialogTitle>Review Your RCS Campaign</DialogTitle>
        <DialogContent dividers>
          <Box>
            <Typography variant="h6" gutterBottom>Campaign Details</Typography>
            <Grid container spacing={2}>
              {[
                { label: "Campaign Name", value: campaignName || "Untitled campaign" },
                { label: "Category", value: categoryName || "Not selected" },
                { label: "Bots", value: selectedBots.length ? selectedBots.map((b) => b.agent_name).join(", ") : "Not selected" },
                { label: "Template Name", value: selectedTemplate?.templateName || "Not selected" },
                { label: "Template Type", value: selectedTemplate?.templateType || "-" },
                { label: "File", value: previewData?.file?.original_name || selectedFile?.name || selectedDbFile?.filter_name || "No file selected" },
                { label: "Schedule", value: enableDateTime ? `${dateTime || "Not set"} (${timeZone})` : "Immediately" },
                { label: "TTL", value: TTL ? `${TTL} ${TTLTime ? "Hours" : "Days"}` : "Not set" },
                { label: "Split Mode", value: enableSplit ? `Enabled • Batch ${batchSize || 0} • Interval ${timeInterval || 0} min` : "Disabled" },
                { label: "Test Mobile Numbers", value: testMobileNumber || "None" },
              ].map(({ label, value }) => (
                <Grid key={label} size={4}>
                  <Paper variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary">{label}</Typography>
                    <Typography variant="subtitle2" fontWeight="bold">{toText(value)}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {Object.keys(templateVariables).length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>Template Variables</Typography>
                <Grid container spacing={2}>
                  {Object.entries(templateVariables).map(([key, value]) => (
                    <Grid key={key} size={4}>
                      <Paper variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
                        <Typography variant="body2" color="text.secondary">{key.replace(/__occurrence_\d+$/, "")}</Typography>
                        <Typography variant="subtitle2" fontWeight="bold">{toText(value, "-")}</Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {previewId && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>Recipient Preview</Typography>

                {isPreviewLoading ? (
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 3, gap: 1 }}>
                    <CircularProgress size={28} />
                    <Typography variant="caption" color="text.secondary">
                      {previewStatus === "queued" ? "Queued for processing..." : `Processing recipient file... (${previewStatus || "initializing"})`}
                    </Typography>
                  </Box>
                ) : previewStatus === "failed" || previewStatus === "error" ? (
                  <Typography variant="body2" color="error">
                    {toText(
                      previewData?.error ||
                        previewData?.message ||
                        previewCountData?.data?.error ||
                        previewCountData?.data?.message ||
                        previewCountData?.error ||
                        previewCountData?.message,
                      "Preview failed. Please try again."
                    )}
                  </Typography>
                ) : (
                  <>
                    <Grid container spacing={2}>
                      {[
                        { label: "Total Numbers", value: previewData?.total_numbers ?? "-" },
                        { label: "Posted", value: previewData?.results?.total_posted ?? "-" },
                        { label: "Invalid", value: previewData?.results?.total_invalid ?? "-" },
                        { label: "Duplicates", value: previewData?.results?.total_duplicates ?? "-" },
                        { label: "Customer Blacklist", value: previewData?.results?.total_customer_blacklist ?? "-" },
                        { label: "Global Blacklist", value: previewData?.results?.total_global_blacklist ?? "-" },
                        { label: "Sent Last 24h", value: previewData?.results?.last_day_campaign ?? "-" },
                        { label: "Remaining", value: previewData?.results?.total_remains ?? "-" },
                        { label: "RCS Message Rate", value: previewData?.estimation?.rate_per_message ?? "-" },
                        { label: "Estimated Cost", value: previewData?.estimation?.estimated_cost ?? "-" },
                        { label: "Batch Number", value: previewData?.processing?.batch_number ?? "-" },
                        { label: "Processed Rows", value: previewData?.processing?.processed_rows ?? "-" },
                      ].map(({ label, value }) => (
                        <Grid key={label} size={3}>
                          <Paper variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
                            <Typography variant="body2" color="text.secondary">{label}</Typography>
                            <Typography variant="subtitle2" fontWeight="bold">{toText(value, "-")}</Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                    {previewData?.is_campaign_ready === false && (
                      <Typography variant="caption" color="warning.main" sx={{ display: "block", mt: 1 }}>
                        Campaign is not yet ready to send based on this preview.
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreviewDialog}>Edit</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={(e) => {
              setIsPreviewOpen(false);
              handleSubmit(e);
            }}
            disabled={
              isLoading ||
              isSplitLoading ||
              !previewData ||
              ["failed", "error"].includes(previewStatus?.toLowerCase()) ||
              previewData?.is_campaign_ready === false
            }
          >
            {isLoading || isSplitLoading ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* File Selection Modal */}
      <Dialog open={isFileModalOpen} onClose={() => setIsFileModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}><Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Database Files</Typography></DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {dbFilesLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={24} /></Box>
          ) : dbFiles?.data?.length > 0 ? (
            <Box>
              <TableContainer sx={{ maxHeight: 300 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow><TableCell sx={{ fontWeight: 600 }}>#</TableCell><TableCell sx={{ fontWeight: 600 }}>Name</TableCell><TableCell align="center" sx={{ fontWeight: 600 }}>Count</TableCell><TableCell sx={{ fontWeight: 600 }}>Select</TableCell></TableRow>
                  </TableHead>
                  <TableBody>
                    {dbFiles.data.map((file, index) => (
                      <TableRow key={file.path} hover sx={{ backgroundColor: selectedDbFile?.path === file.path ? "#E0F2FE" : "inherit" }}>
                        <TableCell>{index + 1}</TableCell><TableCell>{file.filter_name}</TableCell><TableCell align="center">{file.totalcount}</TableCell>
                        <TableCell><Radio size="small" checked={selectedDbFile?.path === file.path} onChange={() => setSelectedDbFile(file)} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, gap: 1 }}>
                <Button onClick={() => setIsFileModalOpen(false)} size="small">Cancel</Button>
                <Button variant="contained" onClick={() => { if (selectedDbFile) { setSelectedFile(null); setIsFileModalOpen(false); } }} size="small" disabled={!selectedDbFile}>
                  Select
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ textAlign: "center", py: 3 }}><Typography variant="body2" color="text.secondary">No database files</Typography></Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Template Selection Drawer */}
      <Drawer anchor="right" open={openDrawer} onClose={toggleDrawer(false)} PaperProps={{ sx: { width: { xs: "100%", sm: "600px" }, background: "rgba(255,255,255,0.7)", backdropFilter: "blur(14px)" } }}>
        {DrawerList}
      </Drawer>
    </Box>
  );
};

export default RCSComponent;